import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FiUpload, FiFileText, FiShoppingCart, FiPackage, FiDollarSign, FiClipboard,
  FiArrowRight, FiArrowLeft, FiCheck, FiAlertTriangle, FiX, FiPlus, FiCamera,
  FiCpu, FiZap, FiSearch, FiEdit2, FiCreditCard, FiRefreshCw
} from 'react-icons/fi';
import { colors } from '../theme/colors';
import { collection, getDocs, addDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import PageLayout from '../components/PageLayout';
import { formatAmount } from '../utils/documentNumbering';
import { createTransaction } from '../utils/transactionHelper';
import { createLinkedAccount, ensureParentAccountExists } from '../utils/coaInitializer';
import { navigateToTransaction } from '../utils/navigationHelper';
import * as XLSX from 'xlsx';
import { readSerialGroups, attachSerials, splitSerials } from '../utils/excelSerials';

const DOC_TYPES = [
  // Purchases
  { code: 'PI', label: 'Purchase Invoice', desc: 'Vendor invoice / bill', icon: FiFileText, color: '#E53E3E', partyType: 'vendor' },
  { code: 'NPI', label: 'Non-Tax Purchase Invoice', desc: 'Vendor bill without GST', icon: FiFileText, color: '#C53030', partyType: 'vendor' },
  { code: 'IPI', label: 'Import Purchase Invoice', desc: 'Foreign vendor / GD-cleared', icon: FiPackage, color: '#6B46C1', partyType: 'vendor' },
  { code: 'PO', label: 'Purchase Order', desc: 'Order to vendor', icon: FiShoppingCart, color: '#DD6B20', partyType: 'vendor' },
  { code: 'GRN', label: 'Goods Receipt', desc: 'Delivery note from vendor', icon: FiPackage, color: '#38A169', partyType: 'vendor' },
  { code: 'PR', label: 'Purchase Return', desc: 'Debit note to vendor', icon: FiRefreshCw, color: '#B7791F', partyType: 'vendor' },
  { code: 'NPR', label: 'Non-Tax Purchase Return', desc: 'Purchase return without GST', icon: FiRefreshCw, color: '#975A16', partyType: 'vendor' },
  // Sales
  { code: 'SI', label: 'Sale Invoice', desc: 'Invoice to customer', icon: FiDollarSign, color: '#3182CE', partyType: 'customer' },
  { code: 'NSI', label: 'Non-Tax Sale Invoice', desc: 'Sale without GST', icon: FiDollarSign, color: '#2C5282', partyType: 'customer' },
  { code: 'SO', label: 'Sale Order', desc: 'Customer purchase order', icon: FiClipboard, color: '#805AD5', partyType: 'customer' },
  { code: 'QT', label: 'Quotation', desc: 'Quote for customer', icon: FiFileText, color: '#D69E2E', partyType: 'customer' },
  { code: 'DC', label: 'Delivery Challan', desc: 'Goods dispatched to customer', icon: FiPackage, color: '#319795', partyType: 'customer' },
  { code: 'SR', label: 'Sale Return', desc: 'Credit note to customer', icon: FiRefreshCw, color: '#9B2C2C', partyType: 'customer' },
  { code: 'NSR', label: 'Non-Tax Sale Return', desc: 'Sale return without GST', icon: FiRefreshCw, color: '#742A2A', partyType: 'customer' },
];

const AIStudio = () => {
  const navigate = useNavigate();
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();

  // Main tab
  const [activeTab, setActiveTab] = useState('import'); // 'import' | 'reconcile'

  // ─── BANK RECONCILIATION STATE ───
  const [reconStep, setReconStep] = useState(1);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [selectedBankName, setSelectedBankName] = useState('');
  const [reconFile, setReconFile] = useState(null);
  const [reconProcessing, setReconProcessing] = useState(false);
  const [reconError, setReconError] = useState('');
  const [reconResult, setReconResult] = useState(null); // { statementSummary, rows }
  const [reconSummary, setReconSummary] = useState('');
  const [reconProgress, setReconProgress] = useState({ current: 0, total: 0 }); // batch progress
  const [banks, setBanks] = useState([]);
  const [erpBankTransactions, setErpBankTransactions] = useState([]);

  // Load banks
  useEffect(() => {
    if (!activeCompany?.id) return;
    const loadBanks = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'banks'), orderBy('bankName')));
        setBanks(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(b => b.isActive !== false));
      } catch (err) { console.error('Error loading banks:', err); }
    };
    loadBanks();
  }, [activeCompany]);

  // Load ERP transactions for selected bank
  const loadBankTransactions = useCallback(async (bankAccountId) => {
    if (!activeCompany?.id || !bankAccountId) return;
    try {
      const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'transactions'));
      const bankTrans = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(t => (t.tranTypeCode === 'BR' || t.tranTypeCode === 'BP') && t.status === 'Posted' && t.bankAccountId === bankAccountId)
        .map(t => ({
          id: t.id,
          docNumber: t.docNumber,
          date: t.docDate,
          type: t.tranTypeCode,
          amount: t.totalAmount || t.netAmount || 0,
          partyName: t.partyName || '',
          chequeNo: t.chequeNo || '',
          description: `${t.tranTypeCode === 'BR' ? 'Receipt' : 'Payment'} - ${t.partyName || ''} ${t.chequeNo ? '(Chq: ' + t.chequeNo + ')' : ''}`
        }));
      setErpBankTransactions(bankTrans);
    } catch (err) { console.error('Error loading bank transactions:', err); }
  }, [activeCompany]);

  const handleBankSelect = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    setSelectedBankId(bankId);
    setSelectedBankName(bank ? `${bank.bankName} - ${bank.accountNumber}` : '');
    if (bankId) loadBankTransactions(bank?.linkedAccountId || bankId);
  };

  const handleReconProcess = async () => {
    if (!reconFile || !selectedBankId || !activeCompany?.id) return;
    setReconProcessing(true);
    setReconError('');
    setReconProgress({ current: 0, total: 0 });

    const processDoc = httpsCallable(functions, 'processInvoiceUpload');
    const callRecon = (text, filePath) => processDoc({
      companyId: activeCompany?.id,
      filePath: filePath || null,
      fileType: reconFile.type,
      targetTranType: 'BANK_RECON',
      excelText: text || undefined,
      bankReconData: {
        bankAccountId: selectedBankId,
        bankAccountName: selectedBankName,
        existingTransactions: erpBankTransactions
      }
    });

    try {
      let excelText = null;
      if (reconFile.name.match(/\.(xlsx|xls|csv)$/i)) {
        const buffer = await reconFile.arrayBuffer();
        const wb = XLSX.read(buffer);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        excelText = XLSX.utils.sheet_to_csv(sheet);
      }

      // Split a large Excel statement into row-batches so the AI's JSON output
      // never exceeds the token limit (which truncated big statements before).
      let batches = null;
      if (excelText) {
        const lines = excelText.split(/\r?\n/);
        // The column-header line names the columns (Date + Debit/Credit/Balance);
        // data rows carry values, not those words — so this finds the real header.
        let headerIdx = lines.findIndex(l => /date/i.test(l) && /(debit|credit|balance|amount|withdraw|deposit)/i.test(l));
        if (headerIdx < 0) headerIdx = 0;
        const headerCtx = lines.slice(0, headerIdx + 1);
        const dataLines = lines.slice(headerIdx + 1).filter(l => l.trim() !== '');
        const BATCH = 40;
        if (dataLines.length > BATCH) {
          batches = [];
          for (let i = 0; i < dataLines.length; i += BATCH) {
            batches.push(headerCtx.concat(dataLines.slice(i, i + BATCH)).join('\n'));
          }
        }
      }

      // ── Single pass (PDF/image, or a small Excel) — unchanged behaviour ──
      if (!batches) {
        let storagePath = null;
        if (!excelText) {
          storagePath = `companies/${activeCompany.id}/ai-uploads/${Date.now()}_${reconFile.name}`;
          await uploadBytes(ref(storage, storagePath), reconFile);
        }
        const result = await callRecon(excelText, storagePath);
        const { answer, action } = result.data;
        if (!action || action.type !== 'BANK_RECONCILIATION') {
          setReconError('AI could not parse the bank statement. ' + (answer || 'Try a clearer file.'));
          setReconProcessing(false);
          return;
        }
        setReconSummary(answer || 'Bank statement processed.');
        setReconResult(action);
        setReconStep(3);
        return;
      }

      // ── Batched pass — process each chunk, then merge into one statement ──
      setReconProgress({ current: 0, total: batches.length });
      let mergedRows = [];
      let summary = null;
      for (let b = 0; b < batches.length; b++) {
        setReconProgress({ current: b + 1, total: batches.length });
        const result = await callRecon(batches[b], null);
        const action = result.data?.action;
        if (action && action.type === 'BANK_RECONCILIATION') {
          if (!summary && action.statementSummary) summary = action.statementSummary;
          if (Array.isArray(action.rows)) mergedRows = mergedRows.concat(action.rows);
        }
      }

      if (mergedRows.length === 0) {
        setReconError('AI could not parse the bank statement. Please try a clearer file or split it.');
        setReconProcessing(false);
        return;
      }

      // Drop exact-duplicate rows (in case header context overlapped), re-number.
      const seen = new Set();
      let rows = mergedRows.filter(r => {
        const k = `${r.date}|${r.debit}|${r.credit}|${r.balance}|${(r.description || '').slice(0, 40)}`;
        if (seen.has(k)) return false; seen.add(k); return true;
      }).map((r, i) => ({ ...r, rowIndex: i }));

      // If two rows matched the SAME ERP transaction across batches, keep the
      // higher-confidence one and un-match the other.
      const bestIdxByTxn = {};
      rows.forEach(r => {
        const id = r.matchedTransactionId;
        if (!id || r.matchStatus === 'unmatched') return;
        if (bestIdxByTxn[id] === undefined || (r.matchConfidence || 0) > (rows[bestIdxByTxn[id]].matchConfidence || 0)) bestIdxByTxn[id] = r.rowIndex;
      });
      rows = rows.map(r => {
        const id = r.matchedTransactionId;
        if (id && r.matchStatus !== 'unmatched' && bestIdxByTxn[id] !== r.rowIndex) {
          return { ...r, matchStatus: 'unmatched', matchedTransactionId: null, matchedDocNumber: null, matchConfidence: 0 };
        }
        return r;
      });

      // Rebuild the whole-statement summary from the merged rows.
      const totalDebits = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
      const totalCredits = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
      const finalSummary = {
        ...(summary || {}),
        closingBalance: Number(rows[rows.length - 1].balance) || (summary?.closingBalance ?? 0),
        totalDebits, totalCredits,
        transactionCount: rows.length,
      };

      setReconSummary(`Bank statement processed in ${batches.length} batches — ${rows.length} transactions.`);
      setReconResult({ type: 'BANK_RECONCILIATION', statementSummary: finalSummary, rows });
      setReconStep(3);
    } catch (err) {
      console.error('Recon error:', err);
      setReconError('Failed to process: ' + (err.message || 'Unknown error'));
    } finally {
      setReconProcessing(false);
      setReconProgress({ current: 0, total: 0 });
    }
  };

  const reconResetAll = () => {
    setReconStep(1); setSelectedBankId(''); setSelectedBankName('');
    setReconFile(null); setReconError(''); setReconResult(null); setReconSummary('');
  };

  // ─── DOCUMENT IMPORT STATE (existing) ───
  const [step, setStep] = useState(1);
  const [targetType, setTargetType] = useState(null);
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState('');
  const [error, setError] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [confidence, setConfidence] = useState(0);

  // Editable extracted data
  const [partyId, setPartyId] = useState('');
  const [partyName, setPartyName] = useState('');
  const [docDate, setDocDate] = useState('');
  const [lines, setLines] = useState([]);
  const [extraFields, setExtraFields] = useState({});
  const [totals, setTotals] = useState({ subtotal: 0, taxAmount: 0, discountAmount: 0, totalAmount: 0 });

  // Unmatched entities
  const [unmatchedParty, setUnmatchedParty] = useState(null);
  const [unmatchedItems, setUnmatchedItems] = useState([]);

  // Master data for matching
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  // Creating state
  const [creating, setCreating] = useState(false);
  const [creatingParty, setCreatingParty] = useState(false);
  const [creatingItem, setCreatingItem] = useState(null); // lineIndex
  // Serial numbers / IMEIs read straight from the uploaded Excel (2026-10-12).
  const [serialNote, setSerialNote] = useState('');          // e.g. "100 serials read from the file"
  const [openSerials, setOpenSerials] = useState(null);      // line index whose serial list is open

  // Load master data
  useEffect(() => {
    if (!activeCompany?.id) return;
    const load = async () => {
      try {
        const [vSnap, cSnap, iSnap] = await Promise.all([
          getDocs(query(collection(db, 'companies', activeCompany.id, 'vendors'), orderBy('vendorName'))),
          getDocs(query(collection(db, 'companies', activeCompany.id, 'customers'), orderBy('companyName'))),
          getDocs(collection(db, 'companies', activeCompany.id, 'inventoryItems'))
        ]);
        setVendors(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setItems(iSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
      } catch (err) { console.error('Error loading master data:', err); }
    };
    load();
  }, [activeCompany]);

  // Step 1: Select document type
  const handleTypeSelect = (type) => {
    setTargetType(type);
    setStep(2);
    setError('');
  };

  // Step 2: Upload file
  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError('File must be under 10MB'); return; }
    setFile(f);
    setError('');
  };

  const handleProcess = async () => {
    if (!file || !targetType || !activeCompany?.id) return;
    setProcessing(true);
    setError('');
    setProcessingMsg('Uploading document...');

    try {
      let excelText = null;
      let serialGroups = null;

      // Parse Excel client-side
      if (file.name.match(/\.(xlsx|xls|csv)$/i)) {
        setProcessingMsg('Reading spreadsheet...');
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        excelText = XLSX.utils.sheet_to_csv(sheet);
        // Serial / IMEI column (if any) — read here, exactly, not by the AI.
        try {
          serialGroups = readSerialGroups(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }));
        } catch (e) { serialGroups = null; }
      }

      // Upload to Firebase Storage
      setProcessingMsg('Analyzing with AI...');
      const storagePath = `companies/${activeCompany.id}/ai-uploads/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);

      if (!excelText) {
        await uploadBytes(storageRef, file);
      }

      // Call Cloud Function
      setProcessingMsg('Extracting data...');
      const processDoc = httpsCallable(functions, 'processInvoiceUpload');
      const result = await processDoc({
        companyId: activeCompany?.id,
        filePath: excelText ? null : storagePath,
        fileType: file.type,
        targetTranType: targetType.code,
        excelText: excelText || undefined
      });

      const { answer, action } = result.data;

      if (!action || !action.transactionData) {
        setError('AI could not extract structured data from this document. ' + (answer || 'Please try a clearer image or PDF.'));
        setProcessing(false);
        return;
      }

      // Populate extracted data
      const td = action.transactionData;
      setAiSummary(answer || 'Document processed successfully.');
      setConfidence(action.confidence || 85);
      setExtractedData(action);
      setPartyId(td.partyId || '');
      setPartyName(td.partyName || '');
      setDocDate(td.docDate || new Date().toISOString().split('T')[0]);
      let newLines = (td.lines || []).map((l, i) => ({
        ...l,
        lineNumber: i + 1,
        unitPrice: l.unitPrice || l.rate || 0,
        discountPercent: l.discountPercent || 0,
        discountAmount: l.discountAmount || 0,
        taxable: l.taxable !== false,
        taxRate: l.taxRate || 0,
        taxAmount: l.taxAmount || 0,
        lineTotal: l.lineTotal || l.amount || 0,
        unitOfMeasure: l.unitOfMeasure || 'Pcs',
        unitCost: l.unitPrice || l.rate || 0
      }));
      // Put the file's serials on the matching lines (by item name / code).
      setSerialNote('');
      setOpenSerials(null);
      if (serialGroups) {
        const res = attachSerials(newLines, serialGroups.groups);
        newLines = res.lines;
        setSerialNote(`${serialGroups.total} serial number(s) read from the file`
          + (res.unmatched ? ` — ${res.unmatched} could not be matched to a line` : ''));
      }
      setLines(newLines);
      setTotals({
        subtotal: td.subtotal || 0,
        taxAmount: td.taxAmount || 0,
        discountAmount: td.discountAmount || 0,
        totalAmount: td.totalAmount || 0,
        paidAmount: td.paidAmount || 0
      });
      setExtraFields({
        vendorInvoiceNo: td.vendorInvoiceNo || '',
        vendorInvoiceDate: td.vendorInvoiceDate || '',
        dueDate: td.dueDate || '',
        paymentTerms: td.paymentTerms || '',
        customerPO: td.customerPO || '',
        deliveryDate: td.deliveryDate || '',
        referenceNo: td.referenceNo || '',
        invoiceNo: td.invoiceNo || '',
        customerRef: td.customerRef || '',
        validUntil: td.validUntil || '',
        notes: td.notes || '',
        // Returns + DC
        reason: td.reason || '',
        parentDocNumber: td.parentDocNumber || '',
        // DC
        vehicleNo: td.vehicleNo || '',
        driverName: td.driverName || '',
        // IPI
        foreignCurrency: td.foreignCurrency || '',
        exchangeRate: td.exchangeRate || '',
        gdNumber: td.gdNumber || '',
        gdDate: td.gdDate || '',
        blAwbNumber: td.blAwbNumber || '',
        countryOfOrigin: td.countryOfOrigin || '',
        portOfDischarge: td.portOfDischarge || ''
      });
      setUnmatchedParty(action.unmatchedParty || (!td.partyId ? { name: td.partyName } : null));
      setUnmatchedItems(action.unmatchedItems || []);

      setStep(3);
    } catch (err) {
      console.error('Process error:', err);
      setError('Failed to process document: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessing(false);
      setProcessingMsg('');
    }
  };

  // Create new vendor/customer
  const handleCreateParty = async () => {
    if (!unmatchedParty?.name || !activeCompany?.id) return;
    setCreatingParty(true);
    try {
      const isVendor = targetType.partyType === 'vendor';
      const collName = isVendor ? 'vendors' : 'customers';
      const nameField = isVendor ? 'vendorName' : 'companyName';

      await ensureParentAccountExists(activeCompany.id, targetType.partyType, currentUser.uid);

      const partyData = {
        [nameField]: unmatchedParty.name,
        phone: unmatchedParty.phone || '',
        ntn: unmatchedParty.ntn || '',
        address: unmatchedParty.address || '',
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      };

      if (isVendor) {
        const existingCount = vendors.length;
        partyData.vendorCode = `VEND-${String(existingCount + 1).padStart(4, '0')}`;
      } else {
        const existingCount = customers.length;
        partyData.customerCode = `CUST-${String(existingCount + 1).padStart(4, '0')}`;
      }

      const docRef = await addDoc(collection(db, 'companies', activeCompany.id, collName), partyData);
      await createLinkedAccount(activeCompany.id, targetType.partyType, docRef.id, unmatchedParty.name, currentUser.uid);

      setPartyId(docRef.id);
      setPartyName(unmatchedParty.name);
      setUnmatchedParty(null);

      // Refresh master data
      if (isVendor) setVendors(prev => [...prev, { id: docRef.id, ...partyData }]);
      else setCustomers(prev => [...prev, { id: docRef.id, ...partyData }]);
    } catch (err) {
      console.error('Error creating party:', err);
      alert('Failed to create: ' + err.message);
    } finally {
      setCreatingParty(false);
    }
  };

  // Create new item
  const handleCreateItem = async (lineIndex) => {
    const line = lines[lineIndex];
    if (!line || !activeCompany?.id) return;
    setCreatingItem(lineIndex);
    try {
      const itemData = {
        itemName: line.itemName || line.description || '',
        itemCode: line.itemCode || `ITEM-${String(items.length + 1).padStart(4, '0')}`,
        purchasePrice: line.unitPrice || 0,
        salePrice: line.unitPrice || 0,
        unitOfMeasure: line.unitOfMeasure || 'Pcs',
        gstRate: line.taxRate || 0,
        // A line that came with serial numbers makes a serial-tracked item.
        trackingType: (line.serialNumbers || []).length > 0 ? 'Serial' : 'None',
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      };
      const docRef = await addDoc(collection(db, 'companies', activeCompany.id, 'inventoryItems'), itemData);

      // Update line with new itemId
      setLines(prev => prev.map((l, i) => i === lineIndex ? { ...l, itemId: docRef.id } : l));
      setItems(prev => [...prev, { id: docRef.id, ...itemData }]);
      setUnmatchedItems(prev => prev.filter(u => u.lineIndex !== lineIndex));
    } catch (err) {
      console.error('Error creating item:', err);
      alert('Failed to create item: ' + err.message);
    } finally {
      setCreatingItem(null);
    }
  };

  // Map to existing party
  const handleMapParty = (id) => {
    const isVendor = targetType.partyType === 'vendor';
    const entity = isVendor ? vendors.find(v => v.id === id) : customers.find(c => c.id === id);
    setPartyId(id);
    setPartyName(isVendor ? entity?.vendorName : entity?.companyName || '');
    setUnmatchedParty(null);
  };

  // Map to existing item
  const handleMapItem = (lineIndex, itemId) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setLines(prev => prev.map((l, i) => i === lineIndex ? {
      ...l, itemId, itemName: item.itemName, itemCode: item.itemCode || '',
      unitOfMeasure: item.unitOfMeasure || 'Pcs'
    } : l));
    setUnmatchedItems(prev => prev.filter(u => u.lineIndex !== lineIndex));
  };

  // Edit line field
  const handleLineEdit = (index, field, value) => {
    setLines(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  // Create the transaction
  const handleCreateEntry = async () => {
    if (!partyId) { alert(`Please select or create a ${targetType.partyType}`); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }

    // Null-guard: every line must resolve to a real item before we post.
    // Posting an item-less line would leave stockFlow with a null itemId and
    // render the ledger unusable for that document.
    const unmappedLines = lines
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => !l.itemId || !String(l.itemId).trim());
    if (unmappedLines.length > 0) {
      const rows = unmappedLines.map(({ i, l }) => `Line ${i + 1}: ${l.description || l.itemName || '(no description)'}`).join('\n');
      alert(`Cannot create entry — ${unmappedLines.length} line(s) aren't mapped to an inventory item:\n\n${rows}\n\nClick "Map Item" or "Create Item" on each unmapped row.`);
      return;
    }

    // Serial numbers: count must match qty, no repeats (asked, not blocked —
    // the entry is saved as a draft and can be fixed on its own screen).
    const serialIssues = [];
    const seenSerials = new Set();
    lines.forEach((l, i) => {
      const sns = l.serialNumbers || [];
      if (!sns.length) return;
      if (sns.length !== (Number(l.quantity) || 0)) serialIssues.push(`Line ${i + 1}: ${sns.length} serial(s) for qty ${Number(l.quantity) || 0}`);
      const rep = sns.filter(s => { const dup = seenSerials.has(s); seenSerials.add(s); return dup; });
      if (rep.length) serialIssues.push(`Line ${i + 1}: repeated serial(s) ${[...new Set(rep)].slice(0, 5).join(', ')}`);
    });
    if (serialIssues.length && !window.confirm(`Please check the serial numbers:\n\n${serialIssues.join('\n')}\n\nCreate the draft anyway?`)) return;

    setCreating(true);
    try {
      const calculatedLines = lines.map((l, i) => ({
        lineNumber: i + 1,
        itemId: l.itemId || '',
        itemCode: l.itemCode || '',
        itemName: l.itemName || '',
        description: l.description || l.itemName || '',
        quantity: Number(l.quantity) || 0,
        unitPrice: Number(l.unitPrice) || 0,
        unitCost: Number(l.unitPrice) || 0,
        discountPercent: Number(l.discountPercent) || 0,
        discountAmount: Number(l.discountAmount) || 0,
        taxable: l.taxable !== false,
        taxRate: Number(l.taxRate) || 0,
        taxAmount: Number(l.taxAmount) || 0,
        lineTotal: Number(l.lineTotal) || 0,
        unitOfMeasure: l.unitOfMeasure || 'Pcs',
        // Serial numbers read from the file. A Purchase Order keeps them so the
        // GRN / invoice made from it fills them in.
        ...((l.serialNumbers || []).length ? {
          serialNumbers: l.serialNumbers,
          trackingType: items.find(it => it.id === l.itemId)?.trackingType || 'Serial'
        } : {})
      }));

      const transactionData = {
        tranTypeCode: targetType.code,
        docDate: docDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        partyType: targetType.partyType,
        partyId: partyId,
        partyName: partyName,
        currency: activeCompany?.currencyCode || 'PKR',
        subtotal: Number(totals.subtotal) || 0,
        discountAmount: Number(totals.discountAmount) || 0,
        taxAmount: Number(totals.taxAmount) || 0,
        totalAmount: Number(totals.totalAmount) || 0,
        status: 'Draft',
        lines: calculatedLines,
        notes: extraFields.notes || '',
        aiImported: true,
        // Type-specific fields
        ...(targetType.code === 'PI' && {
          vendorInvoiceNo: extraFields.vendorInvoiceNo,
          vendorInvoiceDate: extraFields.vendorInvoiceDate,
          dueDate: extraFields.dueDate,
          paymentTerms: extraFields.paymentTerms,
          paidAmount: Number(totals.paidAmount) || 0,
          balanceAmount: (Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0)
        }),
        ...(targetType.code === 'PO' && {
          referenceNo: extraFields.referenceNo,
          deliveryDate: extraFields.deliveryDate,
          paymentTerms: extraFields.paymentTerms
        }),
        ...(targetType.code === 'GRN' && {
          invoiceNo: extraFields.invoiceNo,
          vehicleNo: extraFields.vehicleNo || '',
          driverName: extraFields.driverName || '',
          totalReceived: calculatedLines.reduce((s, l) => s + l.quantity, 0),
          totalAccepted: calculatedLines.reduce((s, l) => s + l.quantity, 0)
        }),
        ...(targetType.code === 'SI' && {
          dueDate: extraFields.dueDate,
          paymentTerms: extraFields.paymentTerms,
          paidAmount: Number(totals.paidAmount) || 0,
          balanceAmount: (Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0)
        }),
        ...(targetType.code === 'SO' && {
          customerPO: extraFields.customerPO,
          deliveryDate: extraFields.deliveryDate
        }),
        ...(targetType.code === 'QT' && {
          customerRef: extraFields.customerRef,
          validUntil: extraFields.validUntil
        }),
        // ── New types ──
        ...(targetType.code === 'NPI' && {
          vendorInvoiceNo: extraFields.vendorInvoiceNo,
          vendorInvoiceDate: extraFields.vendorInvoiceDate,
          dueDate: extraFields.dueDate,
          paymentTerms: extraFields.paymentTerms,
          paidAmount: Number(totals.paidAmount) || 0,
          balanceAmount: (Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0)
        }),
        ...(targetType.code === 'NSI' && {
          dueDate: extraFields.dueDate,
          paymentTerms: extraFields.paymentTerms,
          paidAmount: Number(totals.paidAmount) || 0,
          balanceAmount: (Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0)
        }),
        ...(targetType.code === 'IPI' && {
          vendorInvoiceNo: extraFields.vendorInvoiceNo,
          vendorInvoiceDate: extraFields.vendorInvoiceDate,
          foreignCurrency: extraFields.foreignCurrency || 'USD',
          exchangeRate: Number(extraFields.exchangeRate) || 0,
          gdNumber: extraFields.gdNumber || '',
          gdDate: extraFields.gdDate || '',
          blAwbNumber: extraFields.blAwbNumber || '',
          countryOfOrigin: extraFields.countryOfOrigin || '',
          portOfDischarge: extraFields.portOfDischarge || '',
          paidAmount: Number(totals.paidAmount) || 0,
          balanceAmount: (Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0)
        }),
        ...(targetType.code === 'DC' && {
          parentDocNumber: extraFields.parentDocNumber || '',
          vehicleNo: extraFields.vehicleNo || '',
          driverName: extraFields.driverName || ''
        }),
        ...((targetType.code === 'SR' || targetType.code === 'NSR' ||
             targetType.code === 'PR' || targetType.code === 'NPR') && {
          reason: extraFields.reason || '',
          parentDocNumber: extraFields.parentDocNumber || ''
        })
      };

      // Non-tax variants: force tax off on lines + clear tax totals
      if (['NPI', 'NSI', 'NPR', 'NSR'].includes(targetType.code)) {
        transactionData.lines = transactionData.lines.map(l => ({
          ...l, taxable: false, taxRate: 0, taxAmount: 0,
          lineTotal: (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) - (Number(l.discountAmount) || 0)
        }));
        transactionData.taxAmount = 0;
        transactionData.totalAmount = transactionData.lines.reduce((s, l) => s + (Number(l.lineTotal) || 0), 0);
        transactionData.subtotal = transactionData.totalAmount;
        if (transactionData.balanceAmount != null) {
          transactionData.balanceAmount = transactionData.totalAmount - (Number(totals.paidAmount) || 0);
        }
      }

      const result = await createTransaction(activeCompany.id, transactionData, currentUser.uid);
      navigateToTransaction(navigate, targetType.code, result.id);
    } catch (err) {
      console.error('Error creating transaction:', err);
      alert('Failed to create entry: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    setStep(1); setTargetType(null); setFile(null); setError('');
    setExtractedData(null); setAiSummary(''); setConfidence(0);
    setPartyId(''); setPartyName(''); setDocDate(''); setLines([]);
    setExtraFields({}); setTotals({ subtotal: 0, taxAmount: 0, discountAmount: 0, totalAmount: 0, paidAmount: 0 });
    setUnmatchedParty(null); setUnmatchedItems([]);
    setSerialNote(''); setOpenSerials(null);
  };

  const partyList = targetType?.partyType === 'vendor' ? vendors : customers;
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const currencyCode = activeCompany?.currencyCode || 'PKR';

  return (
    <PageLayout pageTitle="AI Studio" breadcrumb="AI Studio">
      {/* Main Tabs */}
      <MainTabs>
        <MainTab $active={activeTab === 'import'} onClick={() => setActiveTab('import')}>
          <FiUpload size={16} /> Document Import
        </MainTab>
        <MainTab $active={activeTab === 'reconcile'} onClick={() => setActiveTab('reconcile')}>
          <FiCreditCard size={16} /> Bank Reconciliation
        </MainTab>
      </MainTabs>

      {/* ══════════ BANK RECONCILIATION TAB ══════════ */}
      {activeTab === 'reconcile' && (
        <>
          {/* Recon Step Bar */}
          <StepBar>
            <StepItem $active={reconStep >= 1} $done={reconStep > 1}><StepNum>1</StepNum><StepLabel>Select Bank</StepLabel></StepItem>
            <StepLine $active={reconStep > 1} />
            <StepItem $active={reconStep >= 2} $done={reconStep > 2}><StepNum>2</StepNum><StepLabel>Upload Statement</StepLabel></StepItem>
            <StepLine $active={reconStep > 2} />
            <StepItem $active={reconStep >= 3} $done={false}><StepNum>3</StepNum><StepLabel>Review Matches</StepLabel></StepItem>
          </StepBar>

          {/* Recon Step 1: Select Bank */}
          {reconStep === 1 && (
            <Section>
              <SectionTitle><FiCreditCard /> Select Bank Account</SectionTitle>
              <TypeGrid>
                {banks.map(bank => (
                  <TypeCard key={bank.id} onClick={() => { handleBankSelect(bank.id); setReconStep(2); }} $color="#3182CE">
                    <TypeIcon $color="#3182CE"><FiCreditCard size={24} /></TypeIcon>
                    <TypeLabel>{bank.bankName}</TypeLabel>
                    <TypeDesc>{bank.accountNumber} • {bank.accountTitle}</TypeDesc>
                    {bank.isDefault && <TypeDesc style={{ color: '#38A169', fontWeight: 600 }}>Default Account</TypeDesc>}
                  </TypeCard>
                ))}
                {banks.length === 0 && (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: colors.text.secondary }}>
                    No bank accounts found. Please add banks in Master Data first.
                  </div>
                )}
              </TypeGrid>
            </Section>
          )}

          {/* Recon Step 2: Upload Statement */}
          {reconStep === 2 && (
            <Section>
              <BackBtn onClick={() => { setReconStep(1); setReconFile(null); setReconError(''); }}><FiArrowLeft /> Back</BackBtn>
              <SectionTitle><FiUpload /> Upload bank statement for {selectedBankName}</SectionTitle>
              <Card style={{ marginBottom: 16, padding: '12px 16px', fontSize: 13, color: colors.text.secondary }}>
                <FiCpu style={{ marginRight: 8 }} /> ERP has <strong>{erpBankTransactions.length}</strong> posted transactions for this bank account. AI will match them against your statement.
              </Card>
              <UploadZone $hasFile={!!reconFile}>
                {reconFile ? (
                  <FileInfo>
                    <FiFileText size={32} />
                    <div><FileName>{reconFile.name}</FileName><FileSize>{(reconFile.size / 1024).toFixed(0)} KB</FileSize></div>
                    <RemoveBtn onClick={() => setReconFile(null)}><FiX /></RemoveBtn>
                  </FileInfo>
                ) : (
                  <UploadContent>
                    <UploadIcon><FiUpload size={40} /></UploadIcon>
                    <UploadText>Upload bank statement</UploadText>
                    <UploadHint>PDF, Excel (.xlsx, .csv), or screenshot (JPG, PNG)</UploadHint>
                    <input type="file" accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp" onChange={e => { setReconFile(e.target.files?.[0] || null); setReconError(''); }} />
                  </UploadContent>
                )}
              </UploadZone>

              {reconError && <ErrorBox><FiAlertTriangle /> {reconError}</ErrorBox>}

              {reconFile && !reconProcessing && (
                <ProcessBtn onClick={handleReconProcess}><FiZap /> Reconcile with AI</ProcessBtn>
              )}
              {reconProcessing && (
                <ProcessingBox>
                  <Spinner />
                  <ProcessingText>
                    {reconProgress.total > 1
                      ? `Analyzing bank statement… batch ${reconProgress.current} of ${reconProgress.total}`
                      : 'Analyzing bank statement...'}
                  </ProcessingText>
                  <ProcessingHint>
                    {reconProgress.total > 1
                      ? 'Large statement — processing in parts, then merging into one.'
                      : 'AI is matching transactions between your bank and ERP...'}
                  </ProcessingHint>
                </ProcessingBox>
              )}
            </Section>
          )}

          {/* Recon Step 3: Results */}
          {reconStep === 3 && reconResult && (
            <Section>
              <TopBar>
                <BackBtn onClick={() => setReconStep(2)}><FiArrowLeft /> Back</BackBtn>
                <BackBtn onClick={reconResetAll}><FiRefreshCw /> Start Over</BackBtn>
              </TopBar>

              {reconSummary && <AiSummary><FiZap /> {reconSummary}</AiSummary>}

              {/* Statement Summary */}
              {reconResult.statementSummary && (
                <ReconSummaryGrid>
                  <ReconSummaryCard>
                    <ReconSummaryLabel>Period</ReconSummaryLabel>
                    <ReconSummaryValue>{reconResult.statementSummary.periodFrom || '?'} — {reconResult.statementSummary.periodTo || '?'}</ReconSummaryValue>
                  </ReconSummaryCard>
                  <ReconSummaryCard>
                    <ReconSummaryLabel>Transactions</ReconSummaryLabel>
                    <ReconSummaryValue>{reconResult.statementSummary.transactionCount || reconResult.rows?.length || 0}</ReconSummaryValue>
                  </ReconSummaryCard>
                  <ReconSummaryCard $color="#38A169">
                    <ReconSummaryLabel>Matched</ReconSummaryLabel>
                    <ReconSummaryValue>{(reconResult.rows || []).filter(r => r.matchStatus === 'matched').length}</ReconSummaryValue>
                  </ReconSummaryCard>
                  <ReconSummaryCard $color="#E53E3E">
                    <ReconSummaryLabel>Unmatched</ReconSummaryLabel>
                    <ReconSummaryValue>{(reconResult.rows || []).filter(r => r.matchStatus !== 'matched').length}</ReconSummaryValue>
                  </ReconSummaryCard>
                  <ReconSummaryCard>
                    <ReconSummaryLabel>Closing Balance</ReconSummaryLabel>
                    <ReconSummaryValue>{currencyCode} {formatAmount(reconResult.statementSummary.closingBalance || 0)}</ReconSummaryValue>
                  </ReconSummaryCard>
                </ReconSummaryGrid>
              )}

              {/* Reconciliation Table */}
              <Card>
                <CardTitle>Reconciliation Details</CardTitle>
                <TableWrap>
                  <LinesTable>
                    <thead>
                      <tr>
                        <Th style={{ width: 30 }}>#</Th>
                        <Th style={{ width: 40 }}>Status</Th>
                        <Th style={{ width: 100 }}>Date</Th>
                        <Th>Description</Th>
                        <Th style={{ width: 90 }}>Reference</Th>
                        <Th style={{ width: 100, textAlign: 'right' }}>Debit</Th>
                        <Th style={{ width: 100, textAlign: 'right' }}>Credit</Th>
                        <Th style={{ width: 100, textAlign: 'right' }}>Balance</Th>
                        <Th style={{ width: 130 }}>ERP Match</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reconResult.rows || []).map((row, idx) => (
                        <ReconRow key={idx} $status={row.matchStatus}>
                          <Td style={{ textAlign: 'center', color: '#999' }}>{idx + 1}</Td>
                          <Td style={{ textAlign: 'center' }}>
                            {row.matchStatus === 'matched'
                              ? <FiCheck color="#38A169" size={16} />
                              : <FiAlertTriangle color="#E53E3E" size={16} />}
                          </Td>
                          <Td>{row.date}</Td>
                          <Td style={{ fontSize: 12 }}>{row.description}</Td>
                          <Td style={{ fontSize: 11, fontFamily: 'monospace' }}>{row.reference || '-'}</Td>
                          <Td style={{ textAlign: 'right', fontFamily: 'monospace', color: row.debit > 0 ? '#E53E3E' : undefined }}>
                            {row.debit > 0 ? formatAmount(row.debit) : ''}
                          </Td>
                          <Td style={{ textAlign: 'right', fontFamily: 'monospace', color: row.credit > 0 ? '#38A169' : undefined }}>
                            {row.credit > 0 ? formatAmount(row.credit) : ''}
                          </Td>
                          <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            {row.balance != null ? formatAmount(row.balance) : '-'}
                          </Td>
                          <Td>
                            {row.matchStatus === 'matched' ? (
                              <MatchTag $type="matched">{row.matchedDocNumber || 'Matched'} ({row.matchConfidence}%)</MatchTag>
                            ) : (
                              <MatchTag $type={row.suggestedAction === 'create_receipt' ? 'receipt' : 'payment'}>
                                {row.suggestedAction === 'create_receipt' ? 'New Receipt' : row.suggestedAction === 'create_payment' ? 'New Payment' : 'Unmatched'}
                              </MatchTag>
                            )}
                          </Td>
                        </ReconRow>
                      ))}
                    </tbody>
                  </LinesTable>
                </TableWrap>
              </Card>
            </Section>
          )}
        </>
      )}

      {/* ══════════ DOCUMENT IMPORT TAB ══════════ */}
      {activeTab === 'import' && (
      <>
      {/* Step Indicator */}
      <StepBar>
        <StepItem $active={step >= 1} $done={step > 1}><StepNum>1</StepNum><StepLabel>Document Type</StepLabel></StepItem>
        <StepLine $active={step > 1} />
        <StepItem $active={step >= 2} $done={step > 2}><StepNum>2</StepNum><StepLabel>Upload</StepLabel></StepItem>
        <StepLine $active={step > 2} />
        <StepItem $active={step >= 3} $done={false}><StepNum>3</StepNum><StepLabel>Review & Create</StepLabel></StepItem>
      </StepBar>

      {/* Step 1: Select Document Type */}
      {step === 1 && (
        <Section>
          <SectionTitle><FiCpu /> What type of document are you importing?</SectionTitle>
          <TypeGrid>
            {DOC_TYPES.map(t => (
              <TypeCard key={t.code} onClick={() => handleTypeSelect(t)} $color={t.color}>
                <TypeIcon $color={t.color}><t.icon size={24} /></TypeIcon>
                <TypeLabel>{t.label}</TypeLabel>
                <TypeDesc>{t.desc}</TypeDesc>
              </TypeCard>
            ))}
          </TypeGrid>
        </Section>
      )}

      {/* Step 2: Upload File */}
      {step === 2 && (
        <Section>
          <BackBtn onClick={() => { setStep(1); setFile(null); setError(''); }}><FiArrowLeft /> Back</BackBtn>
          <SectionTitle><FiUpload /> Upload your {targetType?.label} document</SectionTitle>
          <UploadZone $hasFile={!!file}>
            {file ? (
              <FileInfo>
                <FiFileText size={32} />
                <div>
                  <FileName>{file.name}</FileName>
                  <FileSize>{(file.size / 1024).toFixed(0)} KB</FileSize>
                </div>
                <RemoveBtn onClick={() => setFile(null)}><FiX /></RemoveBtn>
              </FileInfo>
            ) : (
              <UploadContent>
                <UploadIcon><FiUpload size={40} /></UploadIcon>
                <UploadText>Drop a file here or click to browse</UploadText>
                <UploadHint>Supports PDF, Excel (.xlsx, .csv), and images (JPG, PNG)</UploadHint>
                <input type="file" accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp" onChange={handleFileSelect} />
              </UploadContent>
            )}
          </UploadZone>

          {error && <ErrorBox><FiAlertTriangle /> {error}</ErrorBox>}

          {file && !processing && (
            <ProcessBtn onClick={handleProcess}>
              <FiZap /> Process with AI
            </ProcessBtn>
          )}

          {processing && (
            <ProcessingBox>
              <Spinner />
              <ProcessingText>{processingMsg}</ProcessingText>
              <ProcessingHint>AI is reading your document and extracting data...</ProcessingHint>
            </ProcessingBox>
          )}
        </Section>
      )}

      {/* Step 3: Review & Create */}
      {step === 3 && extractedData && (
        <Section>
          <TopBar>
            <BackBtn onClick={() => { setStep(2); setError(''); }}><FiArrowLeft /> Back</BackBtn>
            <ConfidenceBadge $level={confidence >= 90 ? 'high' : confidence >= 70 ? 'medium' : 'low'}>
              <FiCpu /> AI Confidence: {confidence}%
            </ConfidenceBadge>
          </TopBar>

          {aiSummary && <AiSummary><FiZap /> {aiSummary}</AiSummary>}

          {/* Party Section */}
          <Card>
            <CardTitle>{targetType?.partyType === 'vendor' ? 'Vendor' : 'Customer'}</CardTitle>
            {unmatchedParty ? (
              <UnmatchedBox>
                <UnmatchedLabel><FiAlertTriangle /> Not found in system: <strong>{unmatchedParty.name}</strong></UnmatchedLabel>
                <UnmatchedActions>
                  <CreateBtn onClick={handleCreateParty} disabled={creatingParty}>
                    <FiPlus /> {creatingParty ? 'Creating...' : `Create "${unmatchedParty.name}"`}
                  </CreateBtn>
                  <span>or</span>
                  <SelectWrap>
                    <select value="" onChange={e => handleMapParty(e.target.value)}>
                      <option value="">Map to existing...</option>
                      {partyList.map(p => (
                        <option key={p.id} value={p.id}>{p.vendorName || p.companyName}</option>
                      ))}
                    </select>
                  </SelectWrap>
                </UnmatchedActions>
              </UnmatchedBox>
            ) : (
              <MatchedBox>
                <FiCheck /> <strong>{partyName}</strong>
                <ChangeBtn onClick={() => setUnmatchedParty({ name: partyName })}>Change</ChangeBtn>
              </MatchedBox>
            )}
          </Card>

          {/* Document Details */}
          <Card>
            <CardTitle>Document Details</CardTitle>
            <DetailsGrid>
              <DetailField>
                <label>Date</label>
                <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} />
              </DetailField>
              {(targetType?.code === 'PI' || targetType?.code === 'NPI' || targetType?.code === 'IPI') && (
                <>
                  <DetailField><label>Vendor Invoice #</label><input value={extraFields.vendorInvoiceNo} onChange={e => setExtraFields(p => ({ ...p, vendorInvoiceNo: e.target.value }))} /></DetailField>
                  <DetailField><label>Vendor Invoice Date</label><input type="date" value={extraFields.vendorInvoiceDate} onChange={e => setExtraFields(p => ({ ...p, vendorInvoiceDate: e.target.value }))} /></DetailField>
                </>
              )}
              {targetType?.code === 'SO' && (
                <DetailField><label>Customer PO #</label><input value={extraFields.customerPO} onChange={e => setExtraFields(p => ({ ...p, customerPO: e.target.value }))} /></DetailField>
              )}
              {(['PI', 'NPI', 'IPI', 'SI', 'NSI'].includes(targetType?.code)) && (
                <DetailField><label>Due Date</label><input type="date" value={extraFields.dueDate} onChange={e => setExtraFields(p => ({ ...p, dueDate: e.target.value }))} /></DetailField>
              )}
              {targetType?.code === 'QT' && (
                <DetailField><label>Valid Until</label><input type="date" value={extraFields.validUntil} onChange={e => setExtraFields(p => ({ ...p, validUntil: e.target.value }))} /></DetailField>
              )}
              {/* Returns: reason + parent reference */}
              {['SR', 'NSR', 'PR', 'NPR'].includes(targetType?.code) && (
                <>
                  <DetailField><label>Reason</label><input value={extraFields.reason} onChange={e => setExtraFields(p => ({ ...p, reason: e.target.value }))} placeholder="Why are goods being returned?" /></DetailField>
                  <DetailField><label>Original Doc #</label><input value={extraFields.parentDocNumber} onChange={e => setExtraFields(p => ({ ...p, parentDocNumber: e.target.value }))} placeholder="Original invoice number (optional)" /></DetailField>
                </>
              )}
              {/* Delivery Challan */}
              {targetType?.code === 'DC' && (
                <>
                  <DetailField><label>Sale Order #</label><input value={extraFields.parentDocNumber} onChange={e => setExtraFields(p => ({ ...p, parentDocNumber: e.target.value }))} placeholder="Optional" /></DetailField>
                  <DetailField><label>Vehicle #</label><input value={extraFields.vehicleNo} onChange={e => setExtraFields(p => ({ ...p, vehicleNo: e.target.value }))} /></DetailField>
                  <DetailField><label>Driver Name</label><input value={extraFields.driverName} onChange={e => setExtraFields(p => ({ ...p, driverName: e.target.value }))} /></DetailField>
                </>
              )}
              {/* Import Purchase Invoice */}
              {targetType?.code === 'IPI' && (
                <>
                  <DetailField><label>Foreign Currency</label><input value={extraFields.foreignCurrency} onChange={e => setExtraFields(p => ({ ...p, foreignCurrency: e.target.value }))} placeholder="USD / EUR / CNY" /></DetailField>
                  <DetailField><label>Exchange Rate</label><input type="number" step="0.0001" value={extraFields.exchangeRate} onChange={e => setExtraFields(p => ({ ...p, exchangeRate: e.target.value }))} /></DetailField>
                  <DetailField><label>GD Number</label><input value={extraFields.gdNumber} onChange={e => setExtraFields(p => ({ ...p, gdNumber: e.target.value }))} placeholder="Goods Declaration #" /></DetailField>
                  <DetailField><label>GD Date</label><input type="date" value={extraFields.gdDate} onChange={e => setExtraFields(p => ({ ...p, gdDate: e.target.value }))} /></DetailField>
                  <DetailField><label>BL/AWB #</label><input value={extraFields.blAwbNumber} onChange={e => setExtraFields(p => ({ ...p, blAwbNumber: e.target.value }))} /></DetailField>
                  <DetailField><label>Country of Origin</label><input value={extraFields.countryOfOrigin} onChange={e => setExtraFields(p => ({ ...p, countryOfOrigin: e.target.value }))} /></DetailField>
                  <DetailField><label>Port of Discharge</label><input value={extraFields.portOfDischarge} onChange={e => setExtraFields(p => ({ ...p, portOfDischarge: e.target.value }))} /></DetailField>
                </>
              )}
            </DetailsGrid>
          </Card>

          {/* Line Items */}
          <Card>
            <CardTitle>Line Items ({lines.length})</CardTitle>
            {serialNote && <div data-serial-note style={{ fontSize: 12, color: '#2B6CB0', margin: '-4px 0 8px' }}>{serialNote}</div>}
            <TableWrap>
              <LinesTable>
                <thead>
                  <tr>
                    <Th style={{ width: 30 }}>#</Th>
                    <Th style={{ width: 40 }}>Status</Th>
                    <Th>Item</Th>
                    <Th style={{ width: 70 }}>Qty</Th>
                    <Th style={{ width: 100 }}>Price</Th>
                    <Th style={{ width: 70 }}>{taxLabel} %</Th>
                    <Th style={{ width: 90 }}>Total</Th>
                    <Th style={{ width: 120 }}>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => {
                    const isUnmatched = !line.itemId;
                    const sns = line.serialNumbers || [];
                    const lineItem = line.itemId ? items.find(it => it.id === line.itemId) : null;
                    const snCountOff = sns.length > 0 && sns.length !== (Number(line.quantity) || 0);
                    const snNotTracked = sns.length > 0 && lineItem && lineItem.trackingType !== 'Serial';
                    return (
                      <React.Fragment key={idx}>
                      <tr>
                        <Td style={{ textAlign: 'center', color: '#999' }}>{idx + 1}</Td>
                        <Td style={{ textAlign: 'center' }}>
                          {isUnmatched ? <FiAlertTriangle color="#E53E3E" /> : <FiCheck color="#38A169" />}
                        </Td>
                        <Td>
                          <ItemName $unmatched={isUnmatched}>{line.itemName || line.description}</ItemName>
                          {line.itemCode && <ItemCode>{line.itemCode}</ItemCode>}
                          {sns.length > 0 && (
                            <SerialChip type="button" data-serial-count={sns.length} onClick={() => setOpenSerials(openSerials === idx ? null : idx)}>
                              {sns.length} serial{sns.length === 1 ? '' : 's'} {openSerials === idx ? '▲' : '▼'}
                            </SerialChip>
                          )}
                          {snCountOff && <SerialWarn>{sns.length} serial(s) but qty {Number(line.quantity) || 0}</SerialWarn>}
                          {snNotTracked && <SerialWarn>This item is not serial-tracked — turn on Serial tracking in the item so the GRN / invoice take these serials.</SerialWarn>}
                        </Td>
                        <Td>
                          <MiniInput type="number" value={line.quantity} onChange={e => handleLineEdit(idx, 'quantity', Number(e.target.value))} />
                        </Td>
                        <Td>
                          <MiniInput type="number" value={line.unitPrice} onChange={e => handleLineEdit(idx, 'unitPrice', Number(e.target.value))} />
                        </Td>
                        <Td>
                          <MiniInput type="number" value={line.taxRate} onChange={e => handleLineEdit(idx, 'taxRate', Number(e.target.value))} />
                        </Td>
                        <Td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                          {formatAmount(line.lineTotal)}
                        </Td>
                        <Td>
                          {isUnmatched && (
                            <ItemActions>
                              <MiniBtn onClick={() => handleCreateItem(idx)} disabled={creatingItem === idx}>
                                {creatingItem === idx ? '...' : <><FiPlus size={10} /> Create</>}
                              </MiniBtn>
                              <MiniSelect value="" onChange={e => handleMapItem(idx, e.target.value)}>
                                <option value="">Map...</option>
                                {items.map(it => <option key={it.id} value={it.id}>{it.itemCode ? it.itemCode + ' - ' : ''}{it.itemName}</option>)}
                              </MiniSelect>
                            </ItemActions>
                          )}
                        </Td>
                      </tr>
                      {openSerials === idx && (
                        <tr>
                          <Td colSpan={8} style={{ background: '#F8FAFC' }}>
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>Serial numbers (one per line) — edit if needed</div>
                            <SerialArea
                              defaultValue={sns.join('\n')}
                              rows={Math.min(12, Math.max(4, sns.length))}
                              onBlur={e => handleLineEdit(idx, 'serialNumbers', splitSerials(e.target.value))}
                            />
                          </Td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </LinesTable>
            </TableWrap>
          </Card>

          {/* Totals */}
          <Card>
            <TotalsGrid>
              <TotalRow><span>Subtotal</span><span>{currencyCode} {formatAmount(totals.subtotal)}</span></TotalRow>
              {totals.discountAmount > 0 && <TotalRow><span>Discount</span><span>-{formatAmount(totals.discountAmount)}</span></TotalRow>}
              <TotalRow><span>{taxLabel}</span><span>{formatAmount(totals.taxAmount)}</span></TotalRow>
              <GrandTotal><span>Total</span><span>{currencyCode} {formatAmount(totals.totalAmount)}</span></GrandTotal>
              {totals.paidAmount > 0 && <TotalRow><span>Paid</span><span>-{currencyCode} {formatAmount(totals.paidAmount)}</span></TotalRow>}
              {totals.paidAmount > 0 && <TotalRow><span>Balance Due</span><span>{currencyCode} {formatAmount((Number(totals.totalAmount) || 0) - (Number(totals.paidAmount) || 0))}</span></TotalRow>}
            </TotalsGrid>
          </Card>

          {/* Actions */}
          <ActionBar>
            <BackBtn onClick={reset}><FiX /> Start Over</BackBtn>
            <CreateEntryBtn onClick={handleCreateEntry} disabled={creating || !partyId}>
              {creating ? 'Creating...' : <><FiCheck /> Create {targetType?.label} (Draft)</>}
            </CreateEntryBtn>
          </ActionBar>
        </Section>
      )}
      </>
      )}
    </PageLayout>
  );
};

// ================== STYLED COMPONENTS ==================

const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.5}`;

const Section = styled.div`margin-bottom: 24px;`;

const StepBar = styled.div`display:flex;align-items:center;justify-content:center;gap:0;margin-bottom:32px;padding:20px;background:white;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.1);`;
const StepItem = styled.div`display:flex;align-items:center;gap:8px;opacity:${p => p.$active ? 1 : 0.4};`;
const StepNum = styled.div`width:28px;height:28px;border-radius:50%;background:${colors.primary.main};color:white;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;`;
const StepLabel = styled.span`font-size:13px;font-weight:500;color:${colors.text.primary};`;
const StepLine = styled.div`width:60px;height:2px;background:${p => p.$active ? colors.primary.main : colors.neutral[300]};margin:0 8px;`;

const SectionTitle = styled.h2`font-size:18px;font-weight:600;color:${colors.text.primary};margin-bottom:20px;display:flex;align-items:center;gap:10px;svg{color:${colors.primary.main};}`;

const TypeGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:16px;`;
const TypeCard = styled.div`background:white;border-radius:12px;padding:24px;cursor:pointer;border:2px solid transparent;box-shadow:0 1px 3px rgba(0,0,0,0.08);transition:all 0.2s;&:hover{border-color:${p => p.$color};transform:translateY(-2px);box-shadow:0 4px 12px rgba(0,0,0,0.12);}`;
const TypeIcon = styled.div`width:48px;height:48px;border-radius:12px;background:${p => p.$color}15;color:${p => p.$color};display:flex;align-items:center;justify-content:center;margin-bottom:12px;`;
const TypeLabel = styled.div`font-size:15px;font-weight:600;color:${colors.text.primary};margin-bottom:4px;`;
const TypeDesc = styled.div`font-size:12px;color:${colors.text.secondary};`;

const BackBtn = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;background:white;border:1px solid ${colors.neutral[300]};border-radius:8px;font-size:13px;cursor:pointer;color:${colors.text.secondary};&:hover{background:${colors.neutral[50]};}`;

const UploadZone = styled.div`background:white;border:2px dashed ${p => p.$hasFile ? colors.primary.main : colors.neutral[300]};border-radius:12px;padding:${p => p.$hasFile ? '20px' : '48px 20px'};text-align:center;position:relative;transition:all 0.2s;&:hover{border-color:${colors.primary.main};}`;
const UploadContent = styled.div`input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;}`;
const UploadIcon = styled.div`color:${colors.neutral[400]};margin-bottom:12px;`;
const UploadText = styled.div`font-size:15px;font-weight:500;color:${colors.text.primary};margin-bottom:4px;`;
const UploadHint = styled.div`font-size:12px;color:${colors.text.secondary};`;

const FileInfo = styled.div`display:flex;align-items:center;gap:16px;color:${colors.primary.main};`;
const FileName = styled.div`font-weight:600;font-size:14px;color:${colors.text.primary};`;
const FileSize = styled.div`font-size:12px;color:${colors.text.secondary};`;
const RemoveBtn = styled.button`margin-left:auto;background:none;border:none;color:${colors.error.main};cursor:pointer;padding:4px;`;

const ProcessBtn = styled.button`display:flex;align-items:center;gap:8px;padding:12px 32px;background:#800020;color:white;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;margin:20px auto 0;&:hover{background:#600018;}`;

const ProcessingBox = styled.div`text-align:center;padding:40px;`;
const Spinner = styled.div`width:40px;height:40px;border:3px solid ${colors.neutral[200]};border-top-color:${colors.primary.main};border-radius:50%;animation:${spin} 0.8s linear infinite;margin:0 auto 16px;`;
const ProcessingText = styled.div`font-size:16px;font-weight:500;color:${colors.text.primary};animation:${pulse} 1.5s infinite;`;
const ProcessingHint = styled.div`font-size:12px;color:${colors.text.secondary};margin-top:4px;`;

const ErrorBox = styled.div`display:flex;align-items:center;gap:8px;padding:12px 16px;background:${colors.error.main}10;border:1px solid ${colors.error.main}30;border-radius:8px;color:${colors.error.main};font-size:13px;margin-top:16px;`;

const TopBar = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;`;
const ConfidenceBadge = styled.div`display:flex;align-items:center;gap:6px;padding:6px 14px;border-radius:20px;font-size:13px;font-weight:600;background:${p => p.$level === 'high' ? '#38A16915' : p.$level === 'medium' ? '#D69E2E15' : '#E53E3E15'};color:${p => p.$level === 'high' ? '#38A169' : p.$level === 'medium' ? '#D69E2E' : '#E53E3E'};`;

const AiSummary = styled.div`display:flex;align-items:center;gap:8px;padding:12px 16px;background:#800020 08;border:1px solid #80002020;border-radius:8px;color:#800020;font-size:13px;margin-bottom:16px;svg{flex-shrink:0;}`;

const Card = styled.div`background:white;border-radius:10px;padding:20px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);`;
const CardTitle = styled.h3`font-size:14px;font-weight:600;color:${colors.text.primary};margin:0 0 12px;text-transform:uppercase;letter-spacing:0.5px;`;

const UnmatchedBox = styled.div`background:#FFF5F5;border:1px solid #FED7D7;border-radius:8px;padding:14px;`;
const UnmatchedLabel = styled.div`display:flex;align-items:center;gap:8px;font-size:13px;color:#E53E3E;margin-bottom:10px;`;
const UnmatchedActions = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;span{color:#999;font-size:12px;}`;
const CreateBtn = styled.button`display:flex;align-items:center;gap:4px;padding:6px 14px;background:#38A169;color:white;border:none;border-radius:6px;font-size:12px;font-weight:500;cursor:pointer;&:disabled{opacity:0.5;}`;
const SelectWrap = styled.div`select{padding:6px 10px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:12px;}`;

const MatchedBox = styled.div`display:flex;align-items:center;gap:8px;font-size:14px;color:#38A169;`;
const ChangeBtn = styled.button`margin-left:auto;padding:4px 10px;border:1px solid ${colors.neutral[300]};border-radius:4px;font-size:11px;background:white;cursor:pointer;color:${colors.text.secondary};`;

const DetailsGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px;`;
const DetailField = styled.div`label{display:block;font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;margin-bottom:4px;}input{width:100%;padding:8px 10px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:13px;}`;

const TableWrap = styled.div`overflow-x:auto;`;
const LinesTable = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`text-align:left;padding:8px 10px;font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;border-bottom:2px solid ${colors.neutral[200]};`;
const Td = styled.td`padding:8px 10px;font-size:13px;border-bottom:1px solid ${colors.neutral[100]};vertical-align:middle;`;

const ItemName = styled.div`font-weight:500;color:${p => p.$unmatched ? '#E53E3E' : colors.text.primary};`;
const SerialChip = styled.button`margin-top:4px;padding:1px 8px;font-size:11px;font-weight:600;color:#2B6CB0;background:#EBF8FF;border:1px solid #BEE3F8;border-radius:10px;cursor:pointer;`;
const SerialWarn = styled.div`margin-top:3px;font-size:11px;color:#C53030;`;
const SerialArea = styled.textarea`width:100%;box-sizing:border-box;font-family:monospace;font-size:12px;padding:6px 8px;border:1px solid ${colors.neutral[300]};border-radius:4px;resize:vertical;`;
const ItemCode = styled.div`font-size:11px;color:${colors.text.secondary};font-family:monospace;`;
const MiniInput = styled.input`width:70px;padding:4px 6px;border:1px solid ${colors.neutral[300]};border-radius:4px;font-size:12px;text-align:right;`;
const ItemActions = styled.div`display:flex;gap:4px;`;
const MiniBtn = styled.button`padding:3px 8px;background:#38A169;color:white;border:none;border-radius:4px;font-size:10px;cursor:pointer;display:flex;align-items:center;gap:2px;&:disabled{opacity:0.5;}`;
const MiniSelect = styled.select`padding:3px 6px;border:1px solid ${colors.neutral[300]};border-radius:4px;font-size:10px;max-width:100px;`;

const TotalsGrid = styled.div`max-width:300px;margin-left:auto;`;
const TotalRow = styled.div`display:flex;justify-content:space-between;padding:6px 0;font-size:13px;color:${colors.text.secondary};span:last-child{font-family:monospace;font-weight:500;}`;
const GrandTotal = styled(TotalRow)`border-top:2px solid #800020;padding-top:10px;margin-top:4px;font-size:15px;font-weight:700;color:${colors.text.primary};span:last-child{color:#800020;}`;

const ActionBar = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 0;`;
const CreateEntryBtn = styled.button`display:flex;align-items:center;gap:8px;padding:14px 32px;background:#800020;color:white;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;&:hover{background:#600018;}&:disabled{opacity:0.5;cursor:not-allowed;}`;

// Bank Reconciliation styled components
const MainTabs = styled.div`display:flex;gap:4px;margin-bottom:24px;background:white;padding:6px;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.1);`;
const MainTab = styled.button`flex:1;display:flex;align-items:center;justify-content:center;gap:8px;padding:12px 20px;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;background:${p => p.$active ? '#800020' : 'transparent'};color:${p => p.$active ? 'white' : colors.text.secondary};&:hover{background:${p => p.$active ? '#800020' : colors.neutral[100]};}`;

const ReconSummaryGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px;margin-bottom:16px;`;
const ReconSummaryCard = styled.div`background:white;border-radius:8px;padding:14px 16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);border-left:3px solid ${p => p.$color || '#800020'};`;
const ReconSummaryLabel = styled.div`font-size:11px;color:${colors.text.secondary};text-transform:uppercase;font-weight:600;margin-bottom:4px;`;
const ReconSummaryValue = styled.div`font-size:18px;font-weight:700;color:${colors.text.primary};`;

const ReconRow = styled.tr`background:${p => p.$status === 'matched' ? '#F0FFF4' : '#FFF5F5'};border-bottom:1px solid ${colors.neutral[100]};&:hover{filter:brightness(0.98);}`;
const MatchTag = styled.span`display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;background:${p => p.$type === 'matched' ? '#C6F6D5' : p.$type === 'receipt' ? '#BEE3F8' : '#FED7D7'};color:${p => p.$type === 'matched' ? '#22543D' : p.$type === 'receipt' ? '#2A4365' : '#742A2A'};`;

export default AIStudio;
